import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCharacters, fetchSlotPrice, purchaseCharacterSlot, clearError } from '../store/characterSlice';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import CharacterBuilderModal from './CharacterBuilderModal';
import { PART_LAYOUT } from './CharacterAssemblyPreview';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import '../styles/character.css';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../i18n/format';

function CharacterCardPreview({ character }) {
  return (
    <div className="character-card-mini-assembly">
      <img
        className="character-card-mini-silhouette"
        src="/inventory/character.png"
        alt="character"
      />
      {PART_LAYOUT.map(({ key, left, top, size, z }) => {
        const mod = character[key];
        if (!mod) return null;
        return (
          <img
            key={key}
            className="character-card-mini-part"
            src={resolveAssetUrl(mod.imageUrl)}
            alt={mod.name || key}
            style={{ left, top, width: size, zIndex: z }}
          />
        );
      })}
    </div>
  );
}

export default function CharacterTab({ allModules, onSelectCharacter, selectedCharacterId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { list, slotPrice, isLoading, isPurchasing, error } = useSelector((s) => s.characters);
  const coins = useSelector((s) => s.wallet.datumCoins || 0);

  const [builderCharacter, setBuilderCharacter] = useState(null);

  useEffect(() => {
    dispatch(fetchCharacters());
    dispatch(fetchSlotPrice());
  }, [dispatch]);

  const handlePurchase = async () => {
    const result = await dispatch(purchaseCharacterSlot());
    if (purchaseCharacterSlot.fulfilled.match(result)) {
      setBuilderCharacter(result.payload);
      dispatch(clearError());
      dispatch(fetchWalletBalance());
    }
    // the next slot costs twice as much — and on an error the price may have changed too
    dispatch(fetchSlotPrice());
  };

  const handleCardClick = (character) => {
    if (!character.complete) {
      // Incomplete → open builder
      onSelectCharacter(null);
      setBuilderCharacter(character);
    } else if (selectedCharacterId === character.id) {
      // Second tap on a selected complete hero → details (power, parts, "Send on a mission")
      setBuilderCharacter(character);
    } else {
      // Complete → show in preview
      onSelectCharacter(character);
    }
  };

  const canAfford = slotPrice != null && coins >= slotPrice;

  return (
    <div className="character-tab-content">
      {error && (
        <div style={{ color: '#ff6b6b', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
          {error}
        </div>
      )}

      <div className="character-list">
        {list.map((character) => (
          <button
            key={character.id}
            className={`character-card ${!character.complete ? 'incomplete' : ''} ${selectedCharacterId === character.id ? 'selected' : ''}`}
            onClick={() => handleCardClick(character)}
          >
            <CharacterCardPreview character={character} />
            <div className="character-card-power">⚡{character.power ?? 0}</div>
            <div className={`character-card-badge ${!character.complete ? 'incomplete' : ''} ${character.onMission ? 'on-mission' : ''}`}>
              {character.onMission
                ? t('hero.badge.mission', 'Mission')
                : character.complete ? t('hero.badge.ready', 'Ready') : t('hero.badge.building', 'Building')}
            </div>
          </button>
        ))}

        {/* Buy new slot card */}
        <button
          className={`character-slot-card ${!canAfford || isPurchasing ? 'disabled' : ''}`}
          onClick={handlePurchase}
          disabled={!canAfford || isPurchasing || isLoading}
        >
          <span className="character-slot-plus">+</span>
          <span className="character-slot-label">{t('hero.new_slot', 'New character')}</span>
          <div className="character-slot-price">
            <img
              className="character-slot-price-icon"
              src="/coin_single.png"
              alt="coin"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="character-slot-price-value">{slotPrice != null ? formatNumber(slotPrice) : '…'}</span>
          </div>
        </button>
      </div>

      {list.length === 0 && !isLoading && (
        <div style={{ color: '#9995ca', fontSize: 13, textAlign: 'center', marginTop: 8 }}>
          {t('hero.buy_slot_hint', 'Purchase a hero slot to start building your character!')}
        </div>
      )}

      {builderCharacter && (
        <CharacterBuilderModal
          character={builderCharacter}
          allModules={allModules}
          onClose={() => setBuilderCharacter(null)}
        />
      )}
    </div>
  );
}
